import React, { useEffect, useMemo, useRef, useState, Suspense, lazy } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ref, onValue } from "firebase/database";
import { useCardStatement, cardIdFromSearch, formatMoney, CURRENCY_SYMBOLS } from "./cardsStore";
import { database } from "./firebaseConfig";
import { getStoredChatId } from "./chatId";
import ImageWithCache from "./ImageWithCache";
import CategoryMonthlyChart, { getCategoryMonthly, fmtAmount } from "./CategoryMonthlyChart";
import { categoryStyle } from "./categories";
import {
  generateReceiptNumber,
  generateIban,
  maskCardNumber,
  normalizeCardDigits,
  amountToWordsUA,
  formatReceiptDate,
  formatReceiptDateTime,
  generateAuthCode,
  generateTaxId,
  isMonoTransferAvatar,
  generateCheckToken,
  saveReceipt,
  CHECK_ORIGIN,
  CHECK_DISPLAY_HOST,
} from "./receiptUtils";
import { useMaskBalance } from "./balanceVisibility";
import { hasPremium } from './subscription';
import seededRandom from './seededRandom';
import "./TransactionsDetails.css"

const ReceiptPdfPreview = lazy(() => import("./ReceiptPdfPreview"));

function formatAmount(amount) {
  const isNegative = amount.startsWith('-');
  const numStr = isNegative ? amount.slice(1) : amount;

  let [intPart, decimalPart = "00"] = numStr.split('.');
  if (decimalPart.length === 1) decimalPart += "0";

  let spaceIndex = intPart.length - 3;
  if (intPart.length === 4) spaceIndex = 1;
  else if (intPart.length === 5) spaceIndex = 2;
  else if (intPart.length === 6) spaceIndex = 3;

  if (intPart.length > 3) {
    intPart = intPart.slice(0, spaceIndex) + " " + intPart.slice(spaceIndex);
  }

  const intFormatted = `${isNegative ? "-" : ""}${intPart}`;
  return {
    int: intFormatted,
    dec: `.${decimalPart}`
  };
}

// Спосіб оплати, як у monobank:
//   'online'        — «Оплата в інтернеті» (підписки, ПЗ, цифрові товари: Claude)
//   'online-device' — «Оплата в інтернеті» + пристрій (оплата в застосунках:
//                     таксі, зв'язок, комуналка — Bolt, Київстар)
//   'applepay'      — «Оплата через Apple Pay» + пристрій (решта)
const SUBSCRIPTION_MCC = new Set([4816, 5734, 5735, 5815, 5816, 5817, 5818, 5969, 7372, 7375]);
const IN_APP_MCC = new Set([4121, 4814, 4899, 4900, 5968, 7999]);
function paymentMethod(tx) {
  const mcc = Number(tx?.mcc);
  if (SUBSCRIPTION_MCC.has(mcc)) return 'online';
  if (IN_APP_MCC.has(mcc) || ['taxi', 'utilities'].includes(tx?.category)) return 'online-device';
  return 'applepay';
}

function findTransaction(arr, id) {
  return arr.find((tx) => tx && tx.id && tx.id.toString() === id) || null;
}

// Баланс сразу после каждой транзакции, посчитанный от текущего баланса назад по хронологии
function computeBalanceAfterMap(arr, currentBalance) {
  const valid = arr.filter((tx) => tx && tx.id && tx.name && tx.amount && tx.img && tx.date);
  const sorted = [...valid].sort((a, b) => new Date(b.date) - new Date(a.date)); // от новых к старым

  const balanceAfterById = {};
  let runningBalance = currentBalance;
  for (const tx of sorted) {
    balanceAfterById[tx.id] = runningBalance;
    runningBalance -= Number(tx.amount);
  }
  return balanceAfterById;
}

// Списання при купівлі валюти — у кольорі валютної картки (зі скріншотів
// monobank); шапка — jar-header.png, перефарбований у колір картки
const FX_OUT = {
  USD: {
    header: '/transfer/fx-usd-header.png',
    icon: '/transfer/fx-out-usd.png',
    pill: 'linear-gradient(90deg, #469f8f, #56bd84)',
    wallet: 'linear-gradient(180deg, #44988f, #53b788)',
  },
  EUR: {
    header: '/transfer/fx-eur-header.png',
    icon: '/transfer/fx-out-eur.png',
    pill: 'linear-gradient(90deg, #de454c, #ea5d99)',
    wallet: 'linear-gradient(180deg, #db403d, #ea5c97)',
  },
};

// Платіж за IBAN: банк отримувача — за МФО (цифри 5–10 IBAN). Значок банку
// поверх іконки є лише для ПриватБанку (public/iban-badge.png).
const IBAN_BANKS = {
  '305299': { name: 'ПриватБанк', formal: 'АТ КБ "ПриватБанк"', badge: '/iban-badge.png' },
  '322001': { name: 'Універсал Банк', formal: 'АТ УНІВЕРСАЛ БАНК' },
};
function ibanBank(iban) {
  return IBAN_BANKS[String(iban || '').replace(/\s/g, '').slice(4, 10)] || null;
}

// UA213223130000026007233566001 → «UA 21 322313 00000 2600 7233 5660 01»
function formatIban(iban) {
  const s = String(iban || '').replace(/\s/g, '').toUpperCase();
  if (!/^UA\d{27}$/.test(s)) return s;
  const d = s.slice(2);
  return ['UA', d.slice(0, 2), d.slice(2, 8), d.slice(8, 13), d.slice(13, 17), d.slice(17, 21), d.slice(21, 25), d.slice(25)].join(' ');
}

// UETR — унікальний ідентифікатор платежу (UUID v4), стабільний для транзакції
function generateUetr(txId) {
  const rand = seededRandom(`uetr:${txId}`);
  const hex = (n) => Array.from({ length: n }, () => Math.floor(rand() * 16).toString(16)).join('');
  const variant = (8 + Math.floor(rand() * 4)).toString(16);
  return `${hex(8)}-${hex(4)}-4${hex(3)}-${variant}${hex(3)}-${hex(12)}`;
}

// Квитанції (перегляд PDF і посилання) — лише з Преміумом, див. subscription.js

const TransactionsDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Відкрито зі списку історії — без слайд-анімації (миттєво), щоб уникнути
  // iOS-глюків композитингу під час анімації. З MainPage — зі слайдом.
  const fromHistory = location.state?.from === 'history';
  const chatId = getStoredChatId();

  // Транзакція шукається у виписці своєї картки (?card=<id>; без нього — основна)
  const cardId = cardIdFromSearch(location.search);
  const {
    transactions: allTransactions,
    balance: balanceValue,
    currency,
    card,
  } = useCardStatement(chatId, cardId);
  const currencySymbol = CURRENCY_SYMBOLS[currency] || currency;

  const transaction = useMemo(() => findTransaction(allTransactions, id), [allTransactions, id]);

  // Операція в іншій валюті (з виписки): «У валюті 20.00 $, курс 45.0310»
  const fx = useMemo(() => {
    const opCurrency = transaction?.operationCurrency;
    const opAmount = Math.abs(parseFloat(transaction?.operationAmount));
    if (!opCurrency || opCurrency === currency || !opAmount) return null;
    const rate = Number(transaction.rate) || Math.abs(parseFloat(transaction.amount)) / opAmount;
    return { amount: opAmount.toFixed(2), symbol: CURRENCY_SYMBOLS[opCurrency] || opCurrency, rate: rate.toFixed(4) };
  }, [transaction, currency]);

  // Платіж за IBAN: «Оновити статус» (спершу невідомий) і розгортання реквізитів
  const [ibanStatus, setIbanStatus] = useState('unknown'); // 'unknown' | 'loading' | 'done'
  const [ibanDetailsOpen, setIbanDetailsOpen] = useState(false);
  const [ibanChevronTurns, setIbanChevronTurns] = useState(0);
  const [ibanExtraHeight, setIbanExtraHeight] = useState(0);
  const ibanExtraRef = useRef(null);
  function toggleIbanDetails() {
    // Висота прихованих полів у розгорнутому вигляді — для анімації height:
    // поля + відступ 14px перед кожним (padding-top і gap у .iban-extra.open)
    const inner = ibanExtraRef.current;
    if (inner) {
      const fields = Array.from(inner.children);
      setIbanExtraHeight(fields.reduce((h, el) => h + el.offsetHeight + 14, 0));
    }
    setIbanDetailsOpen((v) => !v);
    setIbanChevronTurns((n) => n + 1);
  }
  const ibanStatusTimeoutRef = useRef(null);
  useEffect(() => () => clearTimeout(ibanStatusTimeoutRef.current), []);
  function refreshIbanStatus() {
    if (ibanStatus !== 'unknown') return;
    setIbanStatus('loading');
    ibanStatusTimeoutRef.current = setTimeout(() => setIbanStatus('done'), 900);
  }

  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [receiptModalClosing, setReceiptModalClosing] = useState(false);
  const receiptModalTimeoutRef = useRef(null);
  const receiptBlobRef = useRef(null); // згенерований PDF квитанції для «Поділитись»
  const RECEIPT_MODAL_ANIM_MS = 300;

  function openReceiptModal() {
    if (!userInfo.hasSub) return; // без Преміуму кнопка не реагує
    setReceiptModalOpen(true);
  }

  function closeReceiptModal() {
    setReceiptModalClosing(true);
    receiptModalTimeoutRef.current = setTimeout(() => {
      setReceiptModalOpen(false);
      setReceiptModalClosing(false);
    }, RECEIPT_MODAL_ANIM_MS);
  }

  useEffect(() => {
    return () => {
      if (receiptModalTimeoutRef.current) clearTimeout(receiptModalTimeoutRef.current);
    };
  }, []);

  // Закриття сторінки зі слайд-аутом (як documents-modal): спершу програємо
  // анімацію вправо, потім реально повертаємось назад.
  const [pageClosing, setPageClosing] = useState(false);
  const closeTimeoutRef = useRef(null);
  const PAGE_ANIM_MS = 300;
  function handleClose() {
    if (pageClosing) return;
    // Відкрито з історії — історія повертається без своєї анімації (вона й
    // так уже «відкрита» під нами)
    if (fromHistory) {
      try { sessionStorage.setItem('skipHistoryAnim', '1'); } catch (e) { /* ignore */ }
    }
    setPageClosing(true);
    closeTimeoutRef.current = setTimeout(() => navigate(-1), PAGE_ANIM_MS);
  }
  useEffect(() => () => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
  }, []);

  // Поки триває слайд-ін шапка тимчасово не sticky — щоб на iOS PWA не було
  // затримки з іконками
  const [animating, setAnimating] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setAnimating(false), PAGE_ANIM_MS);
    return () => clearTimeout(t);
  }, []);

  // Дані користувача для квитанції (ПІБ, IBAN, номер картки відправника)
  // + Преміум (квитанція й посилання на неї — лише з Преміумом).
  const [userInfo, setUserInfo] = useState({ pib: '', iban: '', cardNumber: '', deviceName: '', hasSub: null });
  useEffect(() => {
    if (!chatId) return;
    const userRef = ref(database, `users/${chatId}`);
    const unsubscribe = onValue(userRef, (snapshot) => {
      const user = snapshot.val() || {};
      setUserInfo({
        pib: user.pib || '',
        iban: user.iban || '',
        cardNumber: user.cardNumber || '',
        deviceName: user.deviceName || '',
        hasSub: hasPremium(user),
      });
    });
    return () => unsubscribe();
  }, [chatId]);

  const balanceAfterMap = useMemo(
    () => computeBalanceAfterMap(allTransactions, balanceValue),
    [allTransactions, balanceValue]
  );

  const maskBalance = useMaskBalance();
  const balance = maskBalance(formatMoney(
    transaction ? (balanceAfterMap[transaction.id] ?? balanceValue) : balanceValue,
    currency
  ));


  useEffect(() => {
  const metaViewport = document.querySelector('meta[name="viewport"]');

  // На iOS PWA область під home-indicator (safe-area знизу) фарбується
  // фоном <body>, а не fixed-оверлеєм. Глобально body чорний (#000), тож
  // під сірою деталлю транзакції знизу лишалась чорна смуга. Поки екран
  // відкритий — тимчасово робимо body таким самим, як фон сторінки.
  const originalBodyBg = document.body.style.backgroundColor;
  document.body.style.backgroundColor = '#272727';

  if (metaViewport) {
    const originalContent = metaViewport.getAttribute('content');
    metaViewport.setAttribute(
      'content',
      // maximum-scale=1 + user-scalable=no — забороняємо pinch-zoom сторінки
      // (у standalone PWA це працює, на відміну від звичайного Safari).
      'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover'
    );

    // Немного проскроллим вниз и сразу назад (1 мс задержка)
    const scrollTimeout = setTimeout(() => {
      window.scrollBy(0, 1);
      setTimeout(() => {
        window.scrollBy(0, -1);
      }, 10);
    }, 10);

    return () => {
      metaViewport.setAttribute('content', originalContent);
      document.body.style.backgroundColor = originalBodyBg;
      clearTimeout(scrollTimeout);
    };
  }

  return () => {
    document.body.style.backgroundColor = originalBodyBg;
  };
}, []);



// Оформлення категорії (назва, кольори, шапка) — src/categories.js;
// старі ключі (Food, Cafe…) і нові (taxi, travel…) обидва підтримуються
const catStyle = categoryStyle(transaction?.category);

// Переказ Монобанк (bank='mono') — хедер такий самий, як у поповнення
// картки (ADD_TRANSFER): фото як розмитий фон + blur-overlay.
const isMonoTransfer = transaction?.bank === 'mono' || transaction?.bank === 'monobank';
const headerAsPhoto = transaction?.category === 'ADD_TRANSFER' || isMonoTransfer;

// Купівля валюти між своїми картками (fxStore). Списання з картки-джерела
// monobank оформлює як «Переказ на картку» у кольорі валютної картки:
// шапка, кружок з карткою, плашка «На доларову картку» і гаманець.
const isFx = Boolean(transaction?.fx) || /\/transfer\/fx-/.test(String(transaction?.img || ''));
const fxOut = isFx && Number(transaction?.amount) < 0 ? FX_OUT[transaction?.operationCurrency] : null;

const headerBackground = fxOut ? fxOut.header : (headerAsPhoto ? transaction?.img : catStyle.header);

const categoryInfo = fxOut ? {
  label: transaction.name,
  color: fxOut.wallet,
  pill: fxOut.pill,
} : {
  label: catStyle.uk,
  color: catStyle.wallet,
  pill: `linear-gradient(90deg, ${catStyle.pill[0]}, ${catStyle.pill[1]})`,
};

// Витрати цієї категорії по 6 місяцях (для інфографіки та підсумку «за пів року»)
const categoryStats = getCategoryMonthly(allTransactions, transaction?.category);

const isTransferReceipt = transaction?.category === 'Transfer';

// Платіж за IBAN (бот зберігає iban, edrpou, recipient, purpose)
const isIban = transaction?.category === 'iban';
const ibanRecipientBank = isIban ? ibanBank(transaction.iban) : null;

const receiptToken = useMemo(
  () => transaction ? generateCheckToken(transaction.id) : '',
  [transaction]
);
const receiptFields = useMemo(() => {
  if (!transaction) return {};
  return {
    receiptNumber: generateReceiptNumber(transaction.id),
    date: formatReceiptDate(transaction.date),
    senderName: userInfo.pib,
    // Додаткова картка має власні номер і IBAN
    senderIban: card?.iban || userInfo.iban || generateIban(chatId),
    senderCard: maskCardNumber(card?.number || userInfo.cardNumber),
    recipientName: isMonoTransfer || isIban ? transaction.name : undefined,
    recipientBank: isIban
      ? (transaction.recipientBank || ibanRecipientBank?.formal || ibanRecipientBank?.name || '')
      : isMonoTransfer ? 'Універсал Банк' : 'ПриватБанк',
    recipientSystem: 'VISA',
    recipientCard: isIban ? '' : normalizeCardDigits(isMonoTransfer ? transaction.cardNumber : transaction.name),
    amount: Math.abs(Number(transaction.amount) || 0),
    commission: 0,
    amountWords: amountToWordsUA(transaction.amount),
    dateTime: formatReceiptDateTime(transaction.date),
    authCode: generateAuthCode(transaction.id),
    // Платіж за IBAN — «Платіжна інструкція» з реквізитами отримувача
    ...(isIban ? {
      docType: 'iban',
      senderBank: 'АТ УНІВЕРСАЛ БАНК',
      senderCode: generateTaxId(chatId),
      recipientIban: transaction.iban || '',
      recipientCode: transaction.edrpou || '',
      purpose: transaction.purpose || '',
    } : {}),
  };
}, [transaction, userInfo.pib, userInfo.iban, userInfo.cardNumber, chatId, isMonoTransfer, isIban, ibanRecipientBank, card?.iban, card?.number]);

  const receiptPath = `/p/${receiptToken}`;
  const receiptUrl = `${CHECK_ORIGIN}${receiptPath}`;

  // Зберігаємо дані квитанції — за токеном їх покаже сторінка /p/<токен>
  useEffect(() => {
    if (!transaction) return;
    if ((isTransferReceipt || isMonoTransfer || isIban) && userInfo.hasSub && receiptToken && receiptFields.receiptNumber) {
      saveReceipt(receiptToken, receiptFields);
    }
  }, [receiptToken, receiptFields, isTransferReceipt, isMonoTransfer, isIban, userInfo.hasSub, transaction]);

  if (!transaction) {
    return (
      <div style={{ color: 'white', padding: 20 }}>
        <p>Транзакция не найдена.</p>
        <button onClick={() => navigate(-1)}>Назад</button>
      </div>
    );
  }

// Поділитись PDF-квитанцією через системне вікно. Ім'я файлу — номер
// квитанції (напр. 6HMB-1760-07C1-T0EPР.pdf). Працює на secure origin (https).
// Ім'я беремо з file.name — тому НЕ передаємо title (на iOS title перебиває ім'я).
async function shareReceiptPdf() {
  const blob = receiptBlobRef.current;
  if (!blob) return;
  const fileName = `${generateReceiptNumber(transaction.id)}.pdf`;
  const file = new File([blob], fileName, { type: 'application/pdf' });
  try {
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file] });
      return;
    }
  } catch (e) { return; /* користувач скасував */ }
  // Фолбек (десктоп без Web Share): завантаження файлу
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}
const shareReceiptLink = () => {
  if (!userInfo.hasSub) return; // без Преміуму кнопка не реагує
  if (navigator.share) {
    navigator.share({ title: 'Квитанція', url: receiptUrl }).catch(() => {});
  } else if (navigator.clipboard) {
    navigator.clipboard.writeText(receiptUrl).catch(() => {});
  }
};

  function formatUADate(isoDateStr) {
  const date = new Date(isoDateStr);
  
  const monthsUA = [
    "січня", "лютого", "березня", "квітня", "травня", "червня",
    "липня", "серпня", "вересня", "жовтня", "листопада", "грудня"
  ];

  const day = date.getDate();
  const month = monthsUA[date.getMonth()];
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return `${day} ${month} ${year}, ${hours}:${minutes}`;
}

const { int, dec } = formatAmount(transaction.amount);

// Поповнення картки переказом (ADD_TRANSFER): Privat — лише «Поставити
// запитання»; Mono — «Вiдповiсти платежем» + «Поставити запитання».
const isPrivatTopUp = transaction.category === 'ADD_TRANSFER'
  && String(transaction.img || '').includes('private24');
const isMonoTopUp = transaction.category === 'ADD_TRANSFER' && !isPrivatTopUp;
// Купівля валюти (isFx) — як у monobank: поповнення — чорний кружок зі
// стрілкою (у списку — сірий, tx.img) і лише «Поставити запитання»;
// списання (fxOut) — ще й «Переглянути PDF-квитанцiю»
// «Роздiлити оплату» і «Переглянути квитанцiю» ховаємо для обох поповнень
const hideExtraBtns = isPrivatTopUp || isMonoTopUp || isFx;

// Для переказу в заголовку показуємо номер картки в масці 4441114****4435
// (перші 6 цифр + **** + останні 4), інакше — звичайну назву транзакції.
const transferDigits = isTransferReceipt ? normalizeCardDigits(transaction.name) : '';
const displayTitle = fxOut ? 'Переказ на картку' : isTransferReceipt && transferDigits.length >= 11
  ? `${transferDigits.slice(0, 6)}****${transferDigits.slice(-4)}`
  : transaction.name;

// Дії внизу сторінки. Показуємо лише доречні: «Вiдповiсти платежем» має сенс
// тільки для поповнення переказом з Монобанку, а для переказу — ні.
const footerActions = [
  { key: 'split', icon: '/split-icon.png', alt: 'Split', text: isIban ? 'Розділити витрату' : 'Роздiлити оплату', show: !hideExtraBtns },
  { key: 'repeat', icon: '/repeat-transaction.png', alt: 'Repeat', text: 'Повторити платіж', show: !hideExtraBtns },
  { key: 'pdf', icon: '/pdf-generate.png', alt: 'Pdf', text: 'Переглянути PDF-квитанцiю', show: !hideExtraBtns || Boolean(fxOut), textId: 'transaction-receipt', onClick: openReceiptModal },
  { key: 'reply', icon: '/pdf.png', alt: 'Pdf', text: 'Вiдповiсти платежем', show: isMonoTopUp },
  { key: 'regular', icon: '/regularly-icon.png', alt: 'Regularly', text: 'Зробити регулярним', show: !hideExtraBtns },
  { key: 'question', icon: '/question-icon.png', alt: 'Question', text: 'Поставити запитання', show: true },
].filter((a) => a.show);

  return (
    <div className={`transaction-page-overlay ${pageClosing ? 'closing' : ''}`}>
      <div className={`transaction-page-t ${animating ? 'animating' : ''} ${isIban ? 'iban-page' : ''}`}>
        <div className="transaction-sticky-header">
          <div style={headerBackground ? { backgroundImage: `url(${headerBackground})` } : { backgroundColor: catStyle.from }} className={`transaction-header-t ${headerAsPhoto ? 'add-transfer-bg' : ''}`}>
            {headerAsPhoto && <div className="blur-overlay" />}
             <div className="transaction-btn-con">
               <div onClick={handleClose} className="back-bt-t">
                  <img className="back-btn-t" src="/arrow-back.png" alt="Back" />
              </div>
             </div>
          </div>
          <div className="transaction-header-container"></div>
          <div className={`transaction-info-img ${isMonoTransferAvatar(transaction) ? 'avatar-fit' : ''} ${isFx ? 'fx-fit' : ''} ${isIban ? 'iban-fit' : ''}`}>
              {/* IBAN: плоский золотий кружок з будівлею (як у monobank), а не іконка списку */}
              <ImageWithCache src={fxOut ? fxOut.icon : isFx ? '/transfer/fx-detail.png' : isIban ? '/iban-detail-icon.png' : transaction.img} alt={transaction.name} />
              {ibanRecipientBank?.badge && <img className="iban-bank-badge" src={ibanRecipientBank.badge} alt="" />}
          </div>
        </div>
        <div className="transaction-info-con">
            <div className="transaction-info-title">
                <p>{displayTitle}</p>
            </div>
            <div className="transaction-category-con">
                <div className="line-category"></div>
                <div className="category-name-con" style={{ background: categoryInfo.pill }}>
                    {categoryInfo.label}
                    {catStyle.editable && <img src="/pencil.png" alt="Pencil" />}
                </div>
                <div className="line-category"></div>
            </div>
            <div className="transaction-date-con">
                {formatUADate(transaction.date)}
            </div>
            <div className="transaction-balance-con">
                <span className="amount-main">{int}</span>
                <span className="amount-decimal">{dec} {currencySymbol}</span>
                {fx && <p className="transaction-fx">У валюті {fx.amount} {fx.symbol}, курс {fx.rate}</p>}
            </div>
            {/* Коментар до переказу між користувачами — бачать і відправник, і отримувач */}
            {transaction.comment && (
              <div className="tx-comment">
                <div className="tx-comment-icon">
                  <svg viewBox="0 0 24 24">
                    <path d="M12 4.5c-4.7 0-8.5 3.1-8.5 7 0 2 1 3.8 2.7 5.1L5.6 20l3.9-1.8c.8.2 1.6.3 2.5.3 4.7 0 8.5-3.1 8.5-7s-3.8-7-8.5-7z" fill="#fff" />
                  </svg>
                </div>
                <p className="tx-comment-text">{transaction.comment}</p>
              </div>
            )}
        </div>
        <div className={`transaction-moreinfo-con ${transaction.type !== 'expense' || isFx ? 'income' : ''}`}>
            <div className="transaction-element-t tx-notes">
                <div className="tx-notes-row">
                    <img className="tx-notes-ico" src="/tx-doc.png" alt="" />
                    <span>Опис та #теги</span>
                </div>
                <div className="tx-notes-row">
                    <img className="tx-notes-ico" src="/tx-upload.png" alt="" />
                    <span>Завантажити файл</span>
                </div>
            </div>
            {isIban && (
              <div className="transaction-element-t iban-card">
                <div className="iban-row">
                  {ibanStatus === 'done' ? (
                    <div className="iban-ico" style={{ background: categoryInfo.color }}>
                      <svg viewBox="0 0 24 24"><path d="M6.5 12.5l3.8 3.8 7.2-8" fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </div>
                  ) : (
                    <img className="iban-ico" src="/iban-status.png" alt="" />
                  )}
                  <div className="iban-text">
                    <p className="iban-title">Статус платежу</p>
                    <p className="iban-sub">{ibanStatus === 'done' ? 'Платіж виконано' : 'Оновіть, щоб дізнатися'}</p>
                  </div>
                </div>
                {ibanStatus !== 'done' && (
                  <button type="button" className="receipt-link-share iban-refresh" onClick={refreshIbanStatus}>
                    <img className={ibanStatus === 'loading' ? 'spinning' : ''} src="/iban-refresh.png" alt="" />
                    Оновити статус
                  </button>
                )}
              </div>
            )}
             <div className="transaction-element-t">
                <div className="transaction-element-wallet-icon" style={{ background: categoryInfo.color }}>
                  <img className="transaction-element-img-t wallet-ico" src="/tx-wallet.png" alt="Wallet" />
                </div>
                <div className="transaction-element-text">
                    <p className="transaction-txt-t">Залишок</p>
                    <p className="transaction-balance-t">{balance}</p>
                </div>
            </div>
            {isIban && (
              <div className="transaction-element-t iban-card iban-details" onClick={toggleIbanDetails}>
                <div className="iban-row">
                  <img className="iban-ico" src="/iban-details.png" alt="" />
                  <p className="iban-details-title">Деталі платежу</p>
                  {/* Стрілка щоразу крутиться за годинниковою: v → < → ^ → > → v */}
                  <svg className="iban-chevron" style={{ transform: `rotate(${ibanChevronTurns * 180}deg)` }} viewBox="0 0 24 24">
                    <path d="M6 9.5l6 6 6-6" fill="none" stroke="#a8a8a8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div className="iban-fields">
                  <div className="iban-field">
                    <p className="iban-sub">IBAN</p>
                    <p className="iban-value">{formatIban(transaction.iban)}</p>
                  </div>
                  {/* Решта реквізитів: блок плавно росте у висоту, поля «розсуваються» і проявляються */}
                  <div
                    className={`iban-extra ${ibanDetailsOpen ? 'open' : ''}`}
                    style={{ height: ibanDetailsOpen ? ibanExtraHeight : 0 }}
                  >
                    <div className="iban-extra-inner" ref={ibanExtraRef}>
                      {transaction.edrpou && (
                        <div className="iban-field">
                          <p className="iban-sub">ЄДРПОУ</p>
                          <p className="iban-value">{transaction.edrpou}</p>
                        </div>
                      )}
                      {transaction.purpose && (
                        <div className="iban-field">
                          <p className="iban-sub">Призначення</p>
                          <p className="iban-value">{transaction.purpose}</p>
                        </div>
                      )}
                      <div className="iban-field">
                        <p className="iban-sub">UETR</p>
                        <p className="iban-value">{generateUetr(transaction.id)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
             <div className={`transaction-element-t tx-pay ${paymentMethod(transaction) !== 'online' ? 'two-lines' : ''} ${transaction.type !== 'expense' || transaction.category === 'Transfer' || isFx || isIban ? 'none' : ''}`}>
                <img className="transaction-element-img-t tx-visa" src="/tx-visa.png" alt="Visa" />
                {paymentMethod(transaction) === 'online' ? (
                  <p className="transaction-txt-t">Оплата в інтернеті</p>
                ) : (
                  <div className="transaction-element-text">
                      <p className="transaction-txt-t">
                        {paymentMethod(transaction) === 'online-device' ? 'Оплата в інтернеті' : 'Оплата через Apple Pay'}
                      </p>
                      <p className="transaction-balance-t">{userInfo.deviceName || 'iPhone'}</p>
                  </div>
                )}
            </div>
        </div>
        {/* «Витратили … за пiвроку» — лише для витрат; купівлю валюти між
            своїми картками (isFx) monobank без аналітики показує */}
        <div className={`transaction-analytycs-con ${transaction.type !== 'expense' || isFx ? 'none' : ''}`}>
            <div className="transaction-expense-con">
                <div className="line-category"></div>
                <div className="expense-name-con">
                    Витратили {maskBalance(`${fmtAmount(categoryStats.total)} ${currencySymbol}`)} за пiвроку
                </div>
                <div className="line-category"></div>
            </div>
            <div className="transaction-analytic-con">
                <CategoryMonthlyChart months={categoryStats.months} colors={catStyle.chart} />
            </div>
        </div>
        {(isMonoTransfer || isIban) && (
        <div className={`receipt-link-con ${isIban ? 'iban-link' : ''}`}>
            <div className="receipt-link-card">
                <div className="receipt-link-row">
                    {isIban ? (
                      <img className="iban-ico" src="/iban-link.png" alt="" />
                    ) : (
                      <div className="receipt-link-icon">
                          <img src="/link-icon.webp" alt="" />
                      </div>
                    )}
                    <div className="receipt-link-text">
                        <p className="receipt-link-title">Посилання на квитанцію</p>
                        <div className="receipt-link-url">
                            <img src="/copy-icon.webp" alt="" />
                            <span>{`${CHECK_DISPLAY_HOST}${receiptPath}`}</span>
                        </div>
                    </div>
                </div>
                <button type="button" className="receipt-link-share" onClick={shareReceiptLink}>
                    <img src="/share-payment.webp" alt="" />
                    Поділитись
                </button>
            </div>
        </div>
        )}
        <div className={`transaction-footer ${isIban ? 'iban-footer' : ''}`}>
            {footerActions.map((a) => (
              <React.Fragment key={a.key}>
                <div className="transaction-btn" onClick={a.onClick}>
                    <img className="transaction-btn-img" src={a.icon} alt={a.alt} />
                    <p id={a.textId} className="transaction-btn-text">{a.text}</p>
                </div>
                <div className="line-category"></div>
              </React.Fragment>
            ))}
        </div>

        {receiptModalOpen && (
          <div className={`receipt-modal ${receiptModalClosing ? 'closing' : ''}`}>
            <div className="receipt-modal-header">
              <button type="button" className="receipt-back-btn" onClick={closeReceiptModal}>
                <img src="/arrow-back.png" alt="Назад" />
              </button>
              <h1 className="receipt-modal-title">Квитанцiя</h1>
            </div>

            <div className="receipt-card">
              {(isTransferReceipt || isMonoTransfer || isIban) ? (
                userInfo.hasSub === false ? (
                  // Без активної підписки квитанцію не генеруємо (інакше — нескінченне завантаження)
                  <div className="receipt-locked">
                    <p className="receipt-locked-title">🔒 Квитанція недоступна</p>
                    <p className="receipt-locked-sub">Придбайте підписку, щоб переглядати квитанції за переказами.</p>
                  </div>
                ) : (
                  <Suspense fallback={<div className="receipt-pdf-status">Завантаження квитанції…</div>}>
                    <ReceiptPdfPreview fields={receiptFields} onPdfReady={(blob) => { receiptBlobRef.current = blob; }} />
                  </Suspense>
                )
              ) : (
                <div className="receipt-card-placeholder" style={{ background: categoryInfo.color }}></div>
              )}
            </div>

            <div className="receipt-modal-footer">
              <button type="button" className="receipt-share-btn" onClick={shareReceiptPdf}>Поділитися квитанцією</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionsDetails;
